import { useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "services/utils/Status";
import { approveRequests } from "./bulkApprovalRequests";

export const usePendingBulkApproval = ({ rows, canonicalStatus, canApprove, approveRow, rejectRow, onApproved }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const pendingRows = useMemo(
    () => canApprove ? rows.filter((row) => canonicalStatus(row.status) === "Pending") : [],
    [rows, canonicalStatus, canApprove],
  );
  const selectedRows = pendingRows.filter((row) => selectedIds.has(row.id));

  useEffect(() => {
    const eligibleIds = new Set(pendingRows.map((row) => row.id));
    setSelectedIds((previous) => {
      const next = new Set([...previous].filter((id) => eligibleIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [pendingRows]);

  const isAllSelected = pendingRows.length > 0 && selectedRows.length === pendingRows.length;
  const clear = () => { if (!inFlight.current) setSelectedIds(new Set()); };
  const toggleRow = (row) => {
    if (inFlight.current || !pendingRows.some((item) => item.id === row.id)) return;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };
  const toggleAll = () => {
    if (!inFlight.current) setSelectedIds(new Set(isAllSelected ? [] : pendingRows.map((row) => row.id)));
  };
  const updateSelected = async (action, verb) => {
    if (inFlight.current || !canApprove || !selectedRows.length || !action) return false;
    inFlight.current = true;
    setLoading(true);
    try {
      const { approvedIds, failedCount } = await approveRequests(selectedRows, action);
      setSelectedIds((previous) => new Set([...previous].filter((id) => !approvedIds.has(id))));
      showToast(failedCount ? "error" : "success", failedCount
        ? `${approvedIds.size} ${verb}. ${failedCount} requests were not ${verb}; review and retry.`
        : `${approvedIds.size} requests ${verb}.`);
      if (approvedIds.size) onApproved();
      return failedCount === 0;
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };
  const approve = () => updateSelected(approveRow, "approved");
  const reject = (reason) => updateSelected(rejectRow && ((row) => rejectRow(row, reason)), "rejected");

  return {
    loading, approve, reject, clear, toggleRow, toggleAll, isAllSelected,
    hasPending: pendingRows.length > 0,
    selectedCount: selectedRows.length,
    isIndeterminate: selectedRows.length > 0 && !isAllSelected,
    isSelected: (row) => selectedIds.has(row.id),
  };
};
